import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

/**
 * Confirms submission of a batch to Finance. The server re-validates and may
 * reject with a list of blockers (422); those are shown inline so Operations
 * can fix them. `onConfirm` should call the API and throw on failure — a
 * thrown error carrying `body.blockers` is rendered here.
 */
function SubmitToFinanceDialog({ open, batch, onClose, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  const [blockers, setBlockers] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setBlockers([]);
      setError('');
      setSubmitting(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setBlockers([]);
    setError('');
    try {
      await onConfirm();
      // parent closes + refreshes on success
    } catch (e) {
      if (e.body && Array.isArray(e.body.blockers) && e.body.blockers.length) {
        setBlockers(e.body.blockers);
      } else {
        setError(e.message || 'Submission failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Submit to Finance</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Submitting locks this batch from further Operations edits and hands it to Finance for
          review. The batch is recalculated and validated first.
        </DialogContentText>

        {blockers.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>Cannot submit yet — resolve these first:</AlertTitle>
            <List dense disablePadding>
              {blockers.map((b, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0 }}>
                  <ListItemText primary={`• ${b}`} />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit to Finance'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SubmitToFinanceDialog;
